import Svg, {Circle, ClipPath, Defs, G, Path, Rect, SvgProps} from "react-native-svg"
import {Color} from "@/lib/styles";

// Create new icons here:
// https://react-svgr.com/playground/?dimensions=false&jsxRuntime=automatic&native=true&replaceAttrValues=%23fff%3DcurrentColor&typescript=true

export type IconKind =
    "camera" |
    "cancel" |
    "checkbox" |
    "checkboxChecked" |
    "checkmark" |
    "chevronRight" |
    "clock" |
    "date" |
    "delete" |
    "edit" |
    "ellipsis" |
    "event" |
    "heart" |
    "help" |
    "link" |
    "location" |
    "logo" |
    "logout" |
    "message" |
    "notification" |
    "participants" |
    "plus" |
    "radio" |
    "radioSelected" |
    "relation" |
    "remove" |
    "settings" |
    "share" |
    "sticky";

type Props = {
    kind: IconKind,
    size?: number,
    color?: string,
}

export default function Icon({kind, size, color}: Props) {
    let style = {
        width: size || 16,
        height: size || 16,
        color: color || Color.PrimaryText,
    }
    switch (kind) {
        case "camera":
            return <Camera style={style}/>
        case "cancel":
            return <Cancel style={style}/>
        case "checkbox":
            return <Checkbox style={style}/>
        case "checkboxChecked":
            return <CheckboxChecked style={style}/>
        case "checkmark":
            return <Checkmark style={style}/>
        case "chevronRight":
            return <ChevronRight style={style}/>
        case "clock":
            return <Clock style={style}/>
        case "date":
            return <Date style={style}/>
        case "delete":
            return <Delete style={style}/>
        case "edit":
            return <Edit style={style}/>
        case "ellipsis":
            return <Ellipsis style={style}/>
        case "event":
            return <Event style={style}/>
        case "heart":
            return <Heart style={style}/>
        case "help":
            return <Help style={style}/>
        case "link":
            return <Link style={style}/>
        case "logo":
            return <Logo style={style}/>
        case "location":
            return <Location style={style}/>
        case "logout":
            return <Logout style={style}/>
        case "message":
            return <Message style={style}/>
        case "notification":
            return <Notification style={style}/>
        case "participants":
            return <Participants style={style}/>
        case "plus":
            return <Plus style={style}/>
        case "radio":
            return <Radio style={style}/>
        case "radioSelected":
            return <RadioSelected style={style}/>
        case "relation":
            return <Relation style={style}/>
        case "remove":
            return <Remove style={style}/>
        case "settings":
            return <Settings style={style}/>
        case "share":
            return <Share style={style}/>
        case "sticky":
            return <Sticky style={style}/>
        default:
            // @ts-ignore
            const exhaustiveCheck: never = kind;
            throw new Error(`Unhandled color case: ${exhaustiveCheck}`);
    }
}

const Camera = (props: SvgProps) => (
    <Svg viewBox="0 0 32 32"
         fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M16 1a15 15 0 1 0 0 30 15 15 0 0 0 0-30Zm10 21.25a2.5 2.5 0 0 1-2.5 2.5h-15a2.5 2.5 0 0 1-2.5-2.5v-10a2.5 2.5 0 0 1 2.5-2.5h3.75l.863-1.725a1.406 1.406 0 0 1 1.25-.775h3.262a1.406 1.406 0 0 1 1.25.775l.875 1.725h3.75a2.5 2.5 0 0 1 2.5 2.5v10Z"
        />
        <Path
            fill="currentColor"
            d="M16 22.25a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-8.75a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z"
        />
    </Svg>
)

const Cancel = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M3.401 12.793 1.5 10.908l3.616-3.615-3.616-3.599 1.885-1.901L7 5.408l3.599-3.598 1.901 1.884-3.598 3.599 3.598 3.615-1.902 1.885L7 9.194l-3.598 3.598Z"
        />
    </Svg>
)

const Clock = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M7 .438a6.563 6.563 0 1 0 0 13.125A6.563 6.563 0 0 0 7 .438Zm0 11.484a4.922 4.922 0 1 1 0-9.844 4.922 4.922 0 0 1 0 9.844Zm.82-9.024H6.18V7a.82.82 0 0 0 .418.697l2.838 1.64.82-1.426-2.46-1.403v-3.61h.024Z"
        />
    </Svg>
)

const Checkmark = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M11.175 1.531 5.212 7.495 2.825 5.108.437 7.495l4.775 4.774 8.35-8.35-2.387-2.388Z"
        />
    </Svg>
)

const Checkbox = (props: SvgProps) => (
    <Svg fill="none" {...props}>
        <Rect width={15} height={15} x={0.5} y={0.5} stroke="currentColor" rx={3.5}/>
    </Svg>
)

const CheckboxChecked = (props: SvgProps) => (
    <Svg fill="none" {...props}>
        <Rect width={15} height={15} x={0.5} y={0.5} stroke="currentColor" rx={3.5}/>
        <G clipPath="url(#a)">
            <Path
                fill="currentColor"
                d="M10.386 4.875 6.978 8.283 5.614 6.919 4.25 8.283l2.728 2.728 4.772-4.772-1.364-1.364Z"
            />
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M4 4h8v8H4z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const ChevronRight = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="#F6F6F6"
            fillRule="evenodd"
            d="M9.443 23.25 7.3 21.107 16.407 12 7.3 2.893 9.443.75 20.693 12 9.443 23.25Z"
            clipRule="evenodd"
        />
    </Svg>
)

const Date = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <G clipPath="url(#a)">
            <Path
                fill="currentColor"
                d="M10.828 2.673V1.531a1.094 1.094 0 1 0-2.187 0v1.094H5.359V1.531a1.094 1.094 0 0 0-2.187 0v1.142C1.636 2.94.437 4.308.437 5.906v4.375a3.312 3.312 0 0 0 3.282 3.281h6.562a3.311 3.311 0 0 0 3.281-3.28V5.905c0-1.598-1.198-2.966-2.734-3.233ZM3.172 3.791v1.022a1.094 1.094 0 1 0 2.187 0V3.718h3.282v1.094a1.094 1.094 0 0 0 2.187 0V3.79a2.224 2.224 0 0 1 1.64 2.115v1.64H1.532v-1.64a2.224 2.224 0 0 1 1.64-2.115Z"
            />
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M0 0h14v14H0z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const Delete = (props: SvgProps) => Cancel(props);

const Edit = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M10.318 13.563H3.682a3.249 3.249 0 0 1-3.244-3.245V3.682A3.248 3.248 0 0 1 3.682.438h2.771a.547.547 0 1 1 0 1.093H3.682a2.152 2.152 0 0 0-2.15 2.15v6.637a2.152 2.152 0 0 0 2.15 2.15h6.636a2.152 2.152 0 0 0 2.15-2.15V7.547a.547.547 0 1 1 1.095 0v2.771a3.249 3.249 0 0 1-3.245 3.245Z"
        />
        <Path
            fill="currentColor"
            d="M12.6 1.398a3.27 3.27 0 0 0-4.627 0L4.547 4.826a1.13 1.13 0 0 0-.275.443L2.758 9.807a1.134 1.134 0 0 0 1.434 1.43L8.73 9.723a1.13 1.13 0 0 0 .443-.275L12.6 6.021a3.273 3.273 0 0 0 0-4.628v.005Z"
        />
    </Svg>
)

const Ellipsis = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M3 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm6.998 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm6.998 0a2 2 0 1 1 4 0 2 2 0 0 1-4 0Z"
        />
    </Svg>
)

const Event = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <G fill="currentColor" clipPath="url(#a)">
            <Path
                d="M10.828 2.674V1.531a1.094 1.094 0 1 0-2.187 0v1.094H5.359V1.531a1.094 1.094 0 0 0-2.187 0v1.143A3.314 3.314 0 0 0 .437 5.906v4.375a3.311 3.311 0 0 0 3.282 3.281h6.562a3.311 3.311 0 0 0 3.281-3.28V5.905a3.314 3.314 0 0 0-2.734-3.232Zm1.64 7.607a2.187 2.187 0 0 1-2.187 2.188H3.72A2.187 2.187 0 0 1 1.53 10.28V5.906a2.22 2.22 0 0 1 1.64-2.113v1.02a1.094 1.094 0 1 0 2.188 0V3.718h3.282v1.094a1.094 1.094 0 0 0 2.187 0v-1.02a2.22 2.22 0 0 1 1.64 2.113v4.375Z"/>
            <Path
                d="m7.334 6.09.546 1.129a.364.364 0 0 0 .274.202l1.244.183a.372.372 0 0 1 .208.635l-.902.877a.375.375 0 0 0-.107.329l.213 1.24a.372.372 0 0 1-.547.392l-1.115-.585a.367.367 0 0 0-.345 0l-1.115.585a.371.371 0 0 1-.547-.391l.213-1.241a.375.375 0 0 0-.107-.329l-.902-.877a.372.372 0 0 1 .208-.635l1.244-.183a.364.364 0 0 0 .273-.202l.547-1.13a.372.372 0 0 1 .717 0Z"/>
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M0 0h14v14H0z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const Heart = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M16.554 4.768a4.57 4.57 0 0 1 3.265 1.355 4.621 4.621 0 0 1 0 6.53l-7.821 7.822-7.822-7.822a4.621 4.621 0 0 1 3.266-7.885c1.231 0 2.394.48 3.265 1.355l1.29 1.29 1.291-1.29a4.596 4.596 0 0 1 3.266-1.355Zm0-1.824c-1.651 0-3.298.63-4.556 1.888a6.427 6.427 0 0 0-4.556-1.888c-1.647 0-3.298.63-4.556 1.888a6.44 6.44 0 0 0 0 9.112l9.112 9.112 9.112-9.112a6.441 6.441 0 0 0 0-9.112 6.42 6.42 0 0 0-4.556-1.888Z"
        />
    </Svg>
)

const Help = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M12 2.25c-5.383 0-9.75 4.367-9.75 9.75s4.367 9.75 9.75 9.75 9.75-4.367 9.75-9.75S17.383 2.25 12 2.25Zm0 16.278a1.438 1.438 0 1 1 0-2.876 1.438 1.438 0 0 1 0 2.876Zm1.365-5.602c-.102.045-.215.138-.215.268v1.162h-2.3v-1.15c0-1.04.63-1.974 1.605-2.392.41-.179.796-.658.67-1.3a1.146 1.146 0 0 0-1.852-.646c-.268.22-.419.544-.419.89H8.555a3.445 3.445 0 0 1 4.127-3.38 3.449 3.449 0 0 1 .683 6.548Z"
        />
    </Svg>
)

const Link = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M11.375 11.375H7a2.188 2.188 0 0 1-2.188-2.188V7A2.188 2.188 0 0 1 7 4.812v1.641A.547.547 0 0 0 6.453 7v2.188A.547.547 0 0 0 7 9.733h4.375a.547.547 0 0 0 .547-.546V7a.547.547 0 0 0-.547-.547h-1.64v-1.64h1.64A2.187 2.187 0 0 1 13.563 7v2.188a2.188 2.188 0 0 1-2.188 2.187Zm-7.11-3.828h-1.64A.547.547 0 0 1 2.078 7V4.812a.547.547 0 0 1 .547-.546H7a.547.547 0 0 1 .547.546V7A.547.547 0 0 1 7 7.547v1.64A2.188 2.188 0 0 0 9.188 7V4.812A2.188 2.188 0 0 0 7 2.626H2.625A2.188 2.188 0 0 0 .437 4.813V7a2.188 2.188 0 0 0 2.188 2.188h1.64V7.546Z"
        />
    </Svg>
)

const Location = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M10.796 1.977a5.463 5.463 0 0 0-7.729 7.721l3.865 3.864 3.864-3.864a5.471 5.471 0 0 0 0-7.72ZM6.932 8.62a2.785 2.785 0 1 1 2.565-1.715A2.777 2.777 0 0 1 6.932 8.62Z"
        />
    </Svg>
)

const Logo = (props: SvgProps) => (
    <Svg
        viewBox="0 0 48 48"
        {...props}
    >
        <Path
            d="M47.9 36.2c-.1-1.3-.5-2.5-1.2-3.6-.6-1.1-1.5-2.1-2.5-2.9-1.6-1.2-3.6-1.9-5.6-1.8H14.4c-.5 0-1 0-1.5.1-1.8.3-3.5 1.2-4.8 2.5-.2.2-.4.4-.5.7-.6.8-1 1.7-1.2 2.6s-.2 1.9-.1 2.9c.2.8.5 1.6 1 2.4.5.7 1.1 1.3 1.8 1.8.9.6 1.9.9 3 .9.5 0 .9-.1 1.4-.2.6-.2 1.2-.5 1.7-.9s.9-.9 1.2-1.5c.2-.5.4-1 .4-1.6 0-.6-.1-1.1-.3-1.6-.2-.4-.4-.7-.8-.8-.4-.2-.8-.2-1.2 0-.2.1-.4.2-.5.3-.1.1-.3.3-.3.5-.1.2-.1.4-.1.6 0 .2.1.4.2.6 0 .1.1.2.1.4 0 .1 0 .2-.1.3-.1.2-.3.4-.4.5l-.6.3c-.3.1-.7.1-1 0-.3-.1-.7-.2-.9-.4-.4-.2-.7-.6-.9-.9-.2-.4-.4-.8-.5-1.2-.1-.5-.1-1.1 0-1.6s.4-1 .7-1.5c.1-.1.2-.3.3-.4.5-.5 1-.9 1.6-1.2.6-.3 1.3-.4 1.9-.5h.4c1.2 0 2.4.5 3.4 1.2 1.4 1.1 2.3 2.7 2.5 4.4.2 2-.4 3.9-1.6 5.5-1.4 1.7-3.4 2.7-5.5 3-2.4.2-4.7-.5-6.6-2.1-2-1.7-3.2-4.1-3.5-6.7v-.6c0-6.1 3.8-10.8 10.4-10.8 3.3 0 15.1 0 19.6.1.4 0 .7-.1 1.1-.2.3-.1.7-.4.9-.6.3-.3.5-.6.6-.9.1-.3.2-.7.2-1.1V2.8c0-.8-.3-1.5-.8-2s-1.3-.8-2-.8H2.8c-.3 0-.7.1-1 .2S1.1.6.8.8c-.2.3-.4.6-.6 1s-.2.7-.2 1v33.6c.1 1.7.6 3.3 1.4 4.8.8 1.5 1.8 2.8 3.1 3.9C6.7 47 9.4 48 12.2 48H38.1c1.5-.1 2.9-.6 4.2-1.3 1.3-.7 2.4-1.7 3.4-2.8.9-1.1 1.5-2.3 1.9-3.6.4-1.4.5-2.8.3-4.1z"
            fill={"currentColor"}
        />
    </Svg>
)

const Logout = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <G fill="currentColor" clipPath="url(#a)">
            <Path
                d="m12.43 6.712-1.665-1.666a.488.488 0 0 0-.838.35v.66H6.226a1.11 1.11 0 0 0-1.11 1.112 1.112 1.112 0 0 0 1.11 1.11h3.7v.667a.489.489 0 0 0 .839.345l1.664-1.667a.627.627 0 0 0 0-.91Z"/>
            <Path
                fillRule="evenodd"
                d="M1.613 1.657A3.783 3.783 0 0 1 4.289.547H5.4a3.783 3.783 0 0 1 3.785 3.788 1.01 1.01 0 0 1-2.02 0 1.77 1.77 0 0 0-1.765-1.768H4.29a1.763 1.763 0 0 0-1.764 1.768V9.89a1.77 1.77 0 0 0 1.764 1.767H5.4A1.763 1.763 0 0 0 7.164 9.89a1.01 1.01 0 1 1 2.02 0 3.79 3.79 0 0 1-3.785 3.787H4.29A3.783 3.783 0 0 1 .505 9.89V4.335a3.79 3.79 0 0 1 1.108-2.678Z"
                clipRule="evenodd"
            />
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M0 0h14v14H0z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const Message = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M15.523 1.875H8.498a7.713 7.713 0 0 0-3.513 14.598v5.775l4.918-4.918h5.62a7.728 7.728 0 0 0 0-15.455Z"
        />
    </Svg>
)

const Notification = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            fillRule="evenodd"
            d="M5.437 12v-1.313a6.57 6.57 0 0 1 4.809-6.324 1.97 1.97 0 1 1 3.508 0 6.57 6.57 0 0 1 4.809 6.324v1.314c0 2.081 1.854 3.937 3.937 3.937v2.624h-21v-2.624c2.083 0 3.937-1.856 3.937-3.938Zm9.188 7.875a2.626 2.626 0 0 1-5.25 0c0-1.449 5.25-1.449 5.25 0Z"
            clipRule="evenodd"
        />
    </Svg>
)

const Participants = (props: SvgProps) => (
    <Svg viewBox={"0 0 20 20"} fill="none" {...props}>
        <G clipPath="url(#a)">
            <Path
                fill="currentColor"
                d="M12.802 16.691v.898a1.596 1.596 0 0 1-1.596 1.595H2.429A1.596 1.596 0 0 1 .833 17.59v-.906c.002-.58.174-1.146.495-1.628.103-.156.221-.303.351-.438a3.127 3.127 0 0 1 1.472-.838h.036a4.588 4.588 0 1 1 7.293 0c.383.086.745.249 1.065.479a3.004 3.004 0 0 1 1.257 2.433Zm4.543-9.535A4.52 4.52 0 0 0 13.2.833a.798.798 0 1 0 0 1.596 2.924 2.924 0 0 1 2.278 4.76.799.799 0 0 0 .59 1.296 1.995 1.995 0 0 1 1.92 1.995.798.798 0 0 0 1.595 0 3.63 3.63 0 0 0-2.238-3.324Zm-3.191 3.128a4.524 4.524 0 0 0-4.145-6.327.798.798 0 0 0 0 1.596 2.928 2.928 0 0 1 2.924 2.928 2.9 2.9 0 0 1-.646 1.831.798.798 0 0 0 .59 1.297 2.015 2.015 0 0 1 1.92 1.994.798.798 0 0 0 1.595 0 3.627 3.627 0 0 0-2.238-3.319Z"
            />
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M0 0h20v20H0z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const Plus = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M5.352 13.558V.428h2.613v13.13H5.352ZM.098 8.294V5.682h13.13v2.612H.098Z"
        />
    </Svg>
)

const Radio = (props: SvgProps) => (
    <Svg fill="none" {...props}>
        <Rect width={15} height={15} x={0.5} y={0.5} stroke="currentColor" rx={7.5}/>
    </Svg>
)

const RadioSelected = (props: SvgProps) => (
    <Svg fill="none" {...props}>
        <Rect width={15} height={15} x={0.5} y={0.5} stroke="currentColor" rx={7.5}/>
        <G clipPath="url(#a)">
            <Circle cx={8} cy={8} r={3.25} fill="currentColor"/>
        </G>
        <Defs>
            <ClipPath id="a">
                <Path fill="currentColor" d="M4 4h8v8H4z"/>
            </ClipPath>
        </Defs>
    </Svg>
)

const Relation = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            fillRule="evenodd"
            d="M11.508 10.43a6.587 6.587 0 0 0 2.205-1.631 2.485 2.485 0 0 0-2.359-2.293A3.282 3.282 0 0 0 6.74 1.892a4.378 4.378 0 0 1 2.908 5.594c1.039.571 1.774 1.646 1.859 2.879a.82.82 0 0 1 .002.064Z"
            clipRule="evenodd"
        />
        <Path
            fill="currentColor"
            fillRule="evenodd"
            d="M5.938 2.837a3.283 3.283 0 0 1 2.117 5.31 2.485 2.485 0 0 1 2.36 2.293 6.538 6.538 0 0 1-4.916 2.215A6.548 6.548 0 0 1 .583 10.44a2.486 2.486 0 0 1 2.36-2.293 3.282 3.282 0 0 1 2.996-5.31Z"
            clipRule="evenodd"
        />
    </Svg>
)

const Remove = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <G fill="currentColor">
            <Path d="M21.5 12.25a9.25 9.25 0 1 0-9.25 9.25v2C6.037 23.5 1 18.463 1 12.25S6.037 1 12.25 1 23.5 6.037 23.5 12.25 18.463 23.5 12.25 23.5v-2a9.25 9.25 0 0 0 9.25-9.25Z"/>
            <Path d="m9.099 17.43-1.665-1.65 3.165-3.165-3.165-3.15L9.084 7.8l3.165 3.165 3.15-3.15 1.665 1.65-3.15 3.15 3.15 3.165-1.665 1.65-3.15-3.15-3.15 3.15Z"/>
        </G>
    </Svg>
)

const Settings = (props: SvgProps) => (
    <Svg viewBox="0 0 14 14" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="m7.79 2.314 1.041-.75a1.347 1.347 0 0 1 1.972.466c.117.226.165.48.138.733l-.142 1.263c-.033.278.025.56.166.803.142.242.359.434.62.546l1.182.517c.237.102.438.27.58.482a1.293 1.293 0 0 1 0 1.431c-.142.213-.343.38-.58.483l-1.182.512c-.26.113-.477.304-.619.547a1.29 1.29 0 0 0-.167.802l.142 1.263c.027.252-.02.507-.138.733a1.322 1.322 0 0 1-.521.541 1.35 1.35 0 0 1-1.45-.074l-1.042-.75a1.352 1.352 0 0 0-1.575 0l-1.042.75a1.347 1.347 0 0 1-1.972-.467 1.295 1.295 0 0 1-.137-.733l.141-1.263a1.295 1.295 0 0 0-.168-.801A1.326 1.326 0 0 0 2.42 8.8l-1.187-.503a1.324 1.324 0 0 1-.58-.483 1.293 1.293 0 0 1 0-1.43c.142-.213.343-.381.58-.483l1.182-.517c.26-.113.476-.305.618-.548.14-.242.2-.523.168-.8l-.142-1.273c-.027-.252.02-.507.138-.733.117-.226.298-.414.521-.541a1.35 1.35 0 0 1 1.45.074l1.042.75a1.338 1.338 0 0 0 1.58 0ZM4.187 7.052c0 .557.168 1.102.483 1.566.315.463.762.825 1.286 1.038.523.213 1.099.269 1.654.16a2.877 2.877 0 0 0 1.466-.773c.4-.395.673-.897.783-1.444a2.778 2.778 0 0 0-.165-1.63 2.829 2.829 0 0 0-1.056-1.263 2.896 2.896 0 0 0-1.592-.473 2.898 2.898 0 0 0-2.03.832 2.776 2.776 0 0 0-.83 2.004v-.017Z"
        />
    </Svg>
)

const Share = (props: SvgProps) => (
    <Svg viewBox="0 0 24 24" fill="none" {...props}>
        <Path
            fill="currentColor"
            d="M11.015 1.454 6.862 5.606c-.77.77-.224 2.081.862 2.081h1.64v6.343a1.63 1.63 0 0 0 1.632 1.632h2.237a1.63 1.63 0 0 0 1.632-1.632V7.687h1.64c1.087 0 1.632-1.31.862-2.08L13.21 1.453a1.56 1.56 0 0 0-2.2 0h.005Z"
        />
        <Path
            fill="currentColor"
            d="M21.167 17.5a3.666 3.666 0 0 1-3.667 3.667h-11A3.666 3.666 0 0 1 2.833 17.5V9.25a.92.92 0 0 0-.916-.917A.92.92 0 0 0 1 9.25v8.25C1 20.539 3.461 23 6.5 23h11c3.039 0 5.5-2.461 5.5-5.5V9.25a.92.92 0 0 0-.917-.917.92.92 0 0 0-.916.917v8.25Z"
        />
    </Svg>
)

const Sticky = (props: SvgProps) => (
    <Svg viewBox="0 0 16 16" fill="none" {...props}>
        <Path
            fill="#fff"
            d="M10.219 6.18h-.781V3.719c.414 0 .811-.173 1.104-.48.293-.308.458-.726.458-1.16 0-.436-.165-.853-.458-1.16A1.526 1.526 0 0 0 9.438.436H4.75c-.414 0-.812.173-1.105.481a1.683 1.683 0 0 0-.458 1.16c0 .435.165.853.458 1.16.293.308.69.48 1.105.48V6.18h-.781c-.415 0-.812.173-1.105.48a1.683 1.683 0 0 0-.458 1.16c0 .435.165.853.458 1.16.293.308.69.48 1.105.48h2.344l.39 4.102h.781l.391-4.101h2.344c.414 0 .812-.173 1.105-.48.293-.308.457-.726.457-1.16 0-.436-.164-.853-.457-1.16a1.526 1.526 0 0 0-1.105-.481Z"
        />
    </Svg>
)